/** Review state from ReviewSessionDO / denormalized KV session index */
export type ReviewState =
  | 'idle'
  | 'pending'
  | 'fetching'
  | 'reviewing'
  | 'converging'
  | 'paused'
  | 'converged'
  | 'completed'
  | 'max_rounds_reached'
  | 'failed';

/** Finding severity levels */
export type FindingSeverity = 'blocking' | 'issue' | 'nit';

/** Finding resolution status */
export type FindingStatus = 'open' | 'fixed' | 'dismissed' | 'deferred';

/** Summary of a review session for the list view */
export interface ReviewSummary {
  sessionId: string;
  prNumber: number;
  repo: string;
  prTitle: string;
  prUrl: string;
  state: ReviewState;
  currentRound: number;
  maxRounds: number;
  findings: {
    open: number;
    fixed: number;
    dismissed: number;
    /** Computed from dismissed+auto_rejected in convergence report; may be absent from older backend responses */
    deferred?: number;
  };
  lastActivityAt: string;
  epoch: number;
  /** Spec 188 Phase 3 — optional enrichment fields from R2 round checkpoints.
   * Absent on older sessions or when R2 is unavailable — treat as zero/hidden. */
  stepsCompleted?: number;
  stepsTotal?: number;
  totalCostUsd?: number;
  totalDurationMs?: number;
  failedStep?: string | null;
  canRetry?: boolean;
}

/** Individual finding detail */
export interface ReviewFinding {
  id: string;
  severity: FindingSeverity;
  status: FindingStatus;
  message: string;
  file: string;
  line: number;
  reviewer: string;
  round: number;
  githubCommentUrl?: string;
}

/** Round history entry — matches backend RoundHistory contract */
export interface RoundEntry {
  round: number;
  reviewerCount: number;
  findingsCount: number;
  converged: boolean;
  durationMs: number;
  /** Optional enrichments (not guaranteed by backend) */
  state?: ReviewState;
  timestamp?: string;
  reviewers?: Array<{
    name: string;
    status: 'complete' | 'pending' | 'failed';
  }>;
}

/** Detailed review session */
export interface ReviewDetail {
  sessionId: string;
  prNumber: number;
  repo: string;
  prTitle: string;
  prUrl: string;
  state: ReviewState;
  currentRound: number;
  maxRounds: number;
  findings: ReviewFinding[];
  rounds: RoundEntry[];
  epoch: number;
  lastActivityAt: string;
  tlpRisk?: 'red' | 'yellow' | 'green';
  staleDiscardedCount?: number;
}

/** Stuck thresholds in milliseconds */
export const STUCK_THRESHOLDS = {
  OK: 2 * 60 * 1000,
  WARNING: 5 * 60 * 1000,
} as const;

/** Stuck level derived from elapsed time */
export type StuckLevel = 'ok' | 'warning' | 'critical';

/** Assembled prompt context breakdown from GET /api/reviews/:sessionId/context (Phase 19) */
export interface ReviewContextData {
  sessionId: string;
  epoch: number;
  storedAt: string;
  totalTokens: number;
  sectionTokens: Record<string, number>;
  sectionsIncluded: string[];
  sectionsOmitted: string[];
  testFileCount: number;
  importedSignatureCount: number;
  recentPRCount: number;
  tokenBudget: {
    totalTokens: number;
    sectionTokens: Record<string, number>;
    truncatedSections: string[];
    budgetTarget: number;
    withinBudget: boolean;
  };
  /** Only present when fetched with ?include=prompt */
  contextMessage?: string;
}

export function getStuckLevel(elapsedMs: number): StuckLevel {
  if (elapsedMs < STUCK_THRESHOLDS.OK) return 'ok';
  if (elapsedMs < STUCK_THRESHOLDS.WARNING) return 'warning';
  return 'critical';
}

// ─── Spec 188 Phase 4 — debug page ──────────────────────────────────

export type DebugStepStatus = 'completed' | 'failed' | 'pending' | 'skipped';

export interface DebugReviewerResult {
  reviewerId: string;
  provider: string;
  model: string;
  inputTokens: number;
  completionTokens: number;
  /** `null` when the reviewer's model had no rate (spec 261 Phase 3b).
   *
   *  NOTE: this interface is a hand-maintained MIRROR of
   *  `review-service/src/routes/debug-api.ts`, not an import — so widening the
   *  backend type produced no compile error here and the mismatch surfaced
   *  only as a runtime `.toFixed()` on null (raised by Gemini on #9368). A
   *  fourth copy of one contract, which is the pattern spec 261 exists to
   *  remove; recorded rather than unified, since sharing types across the
   *  worker/UI boundary is its own change. */
  costUsd: number | null;
  durationMs: number;
  findingsCount: number;
}

export interface DebugStep {
  name: string;
  status: DebugStepStatus;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number;
  error: string | null;
  /** Step totals stay non-null: the backend sums with `?? 0`, so an unpriced
   *  reviewer contributes nothing rather than making the whole step unknown. */
  costUsd: number;
  retryCount: number;
  reviewerResults?: DebugReviewerResult[];
}

export interface DebugCostByRound {
  round: number;
  costUsd: number;
  /** Per-reviewer entries CAN be null — unlike a sum, a single entry has room
   *  to say "unknown", and 0 would read as "this reviewer was free". */
  reviewerBreakdown: Record<string, number | null>;
}

export interface ReviewDebugData {
  sessionId: string;
  epoch: number;
  prNumber: number;
  repo: string;
  status: 'converged' | 'failed' | 'running';
  totalCostUsd: number;
  steps: DebugStep[];
  costByRound: DebugCostByRound[];
}
